const { Track } = require('livekit-client');
const { LKBase } = require('./lk-base');
const { LK_EVENTS } = require('./lk-events');
const { UI } = require('./lk-ui');

class LKAudio extends LKBase {
  /**
   * Audio-only conference module.
   * @param {HTMLElement} container
   * @param {object} options - LKBase options + audio-specific:
   * @param {boolean} [options.startWithMic=true]
   * @param {boolean} [options.showSpeakingIndicator=true]
   * @param {boolean} [options.showControls=true]
   */
  constructor(container, options) {
    super(container, options);
    this._startWithMic = options.startWithMic !== false;
    this._showSpeaking = options.showSpeakingIndicator !== false;
    this._showControls = options.showControls !== false;
    this._micEnabled = this._startWithMic;
    this._audioElements = new Map(); // identity → HTMLAudioElement
    this._rows = new Map();          // identity → HTMLElement
    this._listEl = null;
    this._controlsEl = null;
  }

  // ─── Public API ──────────────────────────────────────────────────

  async connect() {
    await super.connect();
    if (this._startWithMic) {
      try {
        await this.room.localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        console.warn('[LKAudio] Microphone unavailable:', err.message);
        this._micEnabled = false;
        this._emit(LK_EVENTS.ERROR, { error: err, context: 'enableMicrophone' });
      }
    }
  }

  async toggleMicrophone() {
    if (!this.room) return;
    this._micEnabled = !this._micEnabled;
    await this.room.localParticipant.setMicrophoneEnabled(this._micEnabled);
    this._updateLocalRow();
    if (this._controlsEl) {
      UI.toggleControl(this._controlsEl, 'mic', !this._micEnabled);
    }
  }

  async switchMicrophone(deviceId) {
    if (!this.room) return;
    await this.room.switchActiveDevice('audioinput', deviceId);
    this._emit(LK_EVENTS.DEVICE_CHANGED, { kind: 'audioinput', deviceId });
  }

  getSpeakingParticipants() {
    if (!this.room) return [];
    return this.room.activeSpeakers || [];
  }

  // ─── UI rendering ────────────────────────────────────────────────

  _renderUI() {
    if (!this.container) return;
    this.container.classList.add('lk-container', 'lk-audio-mode');

    // Participant list
    this._listEl = document.createElement('div');
    this._listEl.className = 'lk-audio-list';
    this.container.appendChild(this._listEl);

    // Add local participant
    this._addParticipantRow(this.room.localParticipant, true);

    // Add existing remote participants
    for (const [, p] of this.room.remoteParticipants) {
      this._addParticipantRow(p, false);
    }

    // Controls
    if (this._showControls) {
      this._controlsEl = UI.createControls([
        {
          id: 'mic',
          icon: UI.icons.mic,
          label: 'Toggle microphone',
          onClick: () => this.toggleMicrophone()
        },
        {
          id: 'hangup',
          icon: UI.icons.hangup,
          label: 'Disconnect',
          className: 'lk-ctrl-btn--danger',
          onClick: () => this.disconnect()
        }
      ]);
      this.container.appendChild(this._controlsEl);
      if (!this._micEnabled) {
        UI.toggleControl(this._controlsEl, 'mic', true);
      }
    }
  }

  _cleanupUI() {
    // Detach all audio elements
    for (const [, el] of this._audioElements) {
      el.remove();
    }
    this._audioElements.clear();
    this._rows.clear();
    this._listEl = null;
    this._controlsEl = null;

    if (this.container) {
      this.container.classList.remove('lk-container', 'lk-audio-mode');
      this.container.innerHTML = '';
    }
  }

  _addParticipantRow(participant, isLocal) {
    if (!this._listEl) return;
    const row = UI.createAudioParticipantRow(participant, {
      name: isLocal ? (participant.name || participant.identity) + ' (you)' : undefined
    });
    if (isLocal) row.classList.add('lk-audio-participant--local');
    this._listEl.appendChild(row);
    this._rows.set(participant.identity, row);
    this._updateRowMicStatus(row, participant);
  }

  _removeParticipantRow(participant) {
    const row = this._rows.get(participant.identity);
    if (row) {
      row.remove();
      this._rows.delete(participant.identity);
    }
    // Clean up audio
    const audio = this._audioElements.get(participant.identity);
    if (audio) {
      audio.remove();
      this._audioElements.delete(participant.identity);
    }
  }

  _updateLocalRow() {
    const row = this._rows.get(this.room.localParticipant.identity);
    if (row) this._updateRowMicStatus(row, this.room.localParticipant);
  }

  _updateRowMicStatus(row, participant) {
    const mic = row.querySelector('.lk-audio-mic');
    if (!mic) return;
    const micPub = participant.getTrackPublication(Track.Source.Microphone);
    const muted = !micPub || micPub.isMuted || !micPub.isSubscribed;
    mic.classList.toggle('lk-audio-mic--muted', muted);
  }

  // ─── Room event hooks ────────────────────────────────────────────

  _onParticipantConnected(participant) {
    this._addParticipantRow(participant, false);
  }

  _onParticipantDisconnected(participant) {
    this._removeParticipantRow(participant);
  }

  _onTrackSubscribed(track, publication, participant) {
    if (track.kind === Track.Kind.Audio) {
      const el = UI.attachAudioTrack(track);
      this._audioElements.set(participant.identity, el);
    }
    const row = this._rows.get(participant.identity);
    if (row) this._updateRowMicStatus(row, participant);
  }

  _onTrackUnsubscribed(track, publication, participant) {
    if (track.kind === Track.Kind.Audio) {
      UI.detachAudioTrack(track);
      this._audioElements.delete(participant.identity);
    }
  }

  _onActiveSpeakersChanged(speakers) {
    if (!this._showSpeaking) return;
    // Reset all
    for (const [, row] of this._rows) {
      row.classList.remove('lk-audio-participant--speaking');
    }
    // Set active
    for (const s of speakers) {
      const row = this._rows.get(s.identity);
      if (row) row.classList.add('lk-audio-participant--speaking');
    }
  }
}

module.exports = { LKAudio };
